import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    IconButton,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import { API_BASE_URL } from '../config';

const DARK_BG      = '#1a1d2e';
const DARK_SURFACE = '#242740';
const DARK_BORDER  = 'rgba(255,255,255,0.10)';
const ACCENT       = '#6C63FF';
const TEXT_PRIMARY = '#f0f0f5';
const TEXT_MUTED   = 'rgba(240,240,245,0.55)';

// Accepts either:
//   trip (object with .trip_id and .name)  — from ItineraryPageV2
//   tripId + tripName (primitives)          — from ItineraryPage
const ShareTripModal = ({ open, onClose, trip, tripId: tripIdProp, tripName: tripNameProp, user }) => {

    const resolvedTripId   = trip?.trip_id   ?? tripIdProp;
    const resolvedTripName = trip?.name      ?? tripNameProp ?? '';

    const [recipientCode, setRecipientCode] = useState('');
    const [loading, setLoading]             = useState(false);
    const [error, setError]                 = useState('');
    const [success, setSuccess]             = useState(false);

    const handleShare = async () => {
        const code = recipientCode.trim().toUpperCase();
        if (!code) {
            setError('Please enter a User ID.');
            return;
        }
        if (!resolvedTripId) {
            setError('Trip ID is missing.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/share_trip.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    trip_id: resolvedTripId,
                    shared_with_public_id: code,
                    permission_level: 'contributor',
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
            } else {
                setError(data.message || 'Failed to share trip.');
            }
        } catch (err) {
            setError('Failed to share trip.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setRecipientCode('');
        setError('');
        setSuccess(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                style: { backgroundColor: DARK_BG, color: TEXT_PRIMARY, overflow: 'hidden' },
                sx: {
                    color: TEXT_PRIMARY,
                    borderRadius: 3,
                    border: `1px solid ${DARK_BORDER}`,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                style={{ backgroundColor: DARK_SURFACE }}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${DARK_BORDER}`,
                    px: 2.5,
                    py: 1.75,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            bgcolor: `${ACCENT}22`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <ShareIcon sx={{ fontSize: 17, color: ACCENT }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
                            Share Trip
                        </Typography>
                        {resolvedTripName && (
                            <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                                {resolvedTripName}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <IconButton onClick={handleClose} size="small" sx={{ color: TEXT_MUTED, '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.07)' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                {success ? (
                    /* ── Success state ── */
                    <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center' }}>
                        <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(76,175,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckCircleIcon sx={{ fontSize: 30, color: '#66bb6a' }} />
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: TEXT_PRIMARY }}>
                            Trip shared!
                        </Typography>
                        <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
                            {resolvedTripName
                                ? `"${resolvedTripName}" is now shared with ${recipientCode}.`
                                : `Trip shared with ${recipientCode}.`}
                        </Typography>
                    </Box>
                ) : (
                    /* ── Input state ── */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Your own ID pill */}
                        {user?.public_user_id && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    bgcolor: DARK_SURFACE,
                                    border: `1px solid ${DARK_BORDER}`,
                                    borderRadius: 2,
                                    px: 1.75,
                                    py: 1.1,
                                }}
                            >
                                <BadgeIcon sx={{ fontSize: 16, color: ACCENT, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: 0.6, flexShrink: 0 }}>
                                    Your ID:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 700,
                                        letterSpacing: 3,
                                        color: TEXT_PRIMARY,
                                        bgcolor: 'rgba(108,99,255,0.15)',
                                        border: `1px solid rgba(108,99,255,0.3)`,
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.25,
                                    }}
                                >
                                    {user.public_user_id}
                                </Typography>
                            </Box>
                        )}

                        <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
                            Share the ID of the user you'd like to share this trip with. Their User ID can be found under the burger menu.
                        </Typography>

                        {/* Recipient input */}
                        <TextField
                            fullWidth
                            label="Recipient User ID"
                            placeholder="e.g. 650PI"
                            value={recipientCode}
                            onChange={(e) => {
                                setRecipientCode(e.target.value.toUpperCase());
                                setError('');
                            }}
                            inputProps={{
                                maxLength: 20,
                                style: {
                                    fontFamily: 'monospace',
                                    letterSpacing: 4,
                                    fontWeight: 700,
                                    color: TEXT_PRIMARY,
                                    caretColor: ACCENT,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonAddIcon sx={{ fontSize: 18, color: ACCENT }} />
                                    </InputAdornment>
                                ),
                                style: { backgroundColor: DARK_SURFACE },
                                sx: {
                                    bgcolor: DARK_SURFACE,
                                    borderRadius: 2,
                                    color: TEXT_PRIMARY,
                                    '& input::placeholder': {
                                        color: TEXT_MUTED,
                                        opacity: 1,
                                        letterSpacing: 1,
                                        fontWeight: 400,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: DARK_BORDER,
                                        borderWidth: '1.5px',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.28)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: ACCENT,
                                        borderWidth: '1.5px',
                                    },
                                    '& .MuiInputAdornment-root': {
                                        color: ACCENT,
                                    },
                                },
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: TEXT_MUTED,
                                    '&.Mui-focused': { color: ACCENT },
                                    '&.MuiInputLabel-shrunk': { color: TEXT_MUTED },
                                },
                            }}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleShare(); }}
                        />

                        {/* Inline error */}
                        {error && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#f28b82', bgcolor: 'rgba(242,139,130,0.10)', border: '1px solid rgba(242,139,130,0.25)', borderRadius: 1.5, px: 1.5, py: 1 }}>
                                <ErrorOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                                <Typography variant="caption">{error}</Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                style={{ backgroundColor: DARK_BG }}
                sx={{
                    px: 2.5,
                    py: 2,
                    gap: 1,
                    borderTop: success ? 'none' : `1px solid ${DARK_BORDER}`,
                    mt: success ? 0 : 1,
                }}
            >
                {success ? (
                    <Button
                        onClick={handleClose}
                        fullWidth
                        variant="contained"
                        sx={{
                            bgcolor: ACCENT,
                            color: '#fff',
                            fontWeight: 600,
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#574fd6' },
                        }}
                    >
                        Done
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={handleClose}
                            disabled={loading}
                            sx={{ color: TEXT_MUTED, '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' }, borderRadius: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleShare}
                            disabled={loading || !recipientCode.trim()}
                            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <PersonAddIcon />}
                            sx={{
                                bgcolor: ACCENT,
                                color: '#fff',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 2.5,
                                '&:hover': { bgcolor: '#574fd6' },
                                '&.Mui-disabled': { bgcolor: 'rgba(108,99,255,0.25)', color: 'rgba(255,255,255,0.35)' },
                            }}
                        >
                            Share
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ShareTripModal;
